/**
 * AttentionCard — "Needs your attention"
 * Surfaces review-paused cadences & flagged leads with one-click Approve so
 * the autonomous machine keeps moving without digging into lead pages.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AttentionCard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/brain/cadence-analytics/attention');
      setItems(res.data?.items || []);
    } catch { /* silent — card simply hides */ }
    setLoaded(true);
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (item) => {
    setBusyId(item.lead_id);
    try {
      await api.post(`/api/leads/${item.lead_id}/approve-cadence`);
      setItems((prev) => prev.filter((i) => i.lead_id !== item.lead_id));
      toast.success(`Approved — Brain will resume outreach for ${item.lead_name}`);
    } catch (e) {
      toast.error('Could not approve', { description: e?.response?.data?.detail || '' });
    } finally { setBusyId(null); }
  };

  const reject = async (item) => {
    setBusyId(item.lead_id);
    try {
      await api.post(`/api/leads/${item.lead_id}/reject-cadence`);
      setItems((prev) => prev.filter((i) => i.lead_id !== item.lead_id));
      toast.success(`${item.lead_name} marked Lost — outreach stopped`);
    } catch (e) {
      toast.error('Could not reject', { description: e?.response?.data?.detail || '' });
    } finally { setBusyId(null); }
  };

  if (!loaded || items.length === 0) return null;

  return (
    <Card className="border-amber-500/30 bg-amber-50/30 dark:bg-amber-950/10 overflow-hidden" data-testid="attention-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          Needs your attention
          <Badge variant="outline" className="bg-amber-500/10 border-amber-500/30 text-amber-600 text-[10px]" data-testid="attention-count">{items.length}</Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">The AI Brain paused outreach on these leads and is waiting for your go-ahead.</p>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {items.slice(0, 5).map((item) => (
          <div key={item.lead_id} className="flex items-start gap-3 rounded-lg border border-amber-500/20 bg-background/60 p-3" data-testid={`attention-item-${item.lead_id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">
                {item.lead_name}
                {item.company ? <span className="text-muted-foreground font-normal"> · {item.company}</span> : null}
                {Number.isFinite(item.ai_score) ? <span className="ml-2 text-[10px] text-muted-foreground">AI {item.ai_score}/100</span> : null}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.reason}</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white" disabled={busyId === item.lead_id}
                onClick={() => approve(item)} data-testid={`attention-approve-${item.lead_id}`}>
                {busyId === item.lead_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                Approve & resume
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs text-red-500 border-red-500/30 hover:bg-red-500/10" disabled={busyId === item.lead_id}
                onClick={() => reject(item)} title="Agree with the AI — mark Lost & stop outreach" data-testid={`attention-reject-${item.lead_id}`}>
                <XCircle className="h-3.5 w-3.5 mr-1" />Mark lost
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => navigate(`/leads/${item.lead_id}`)} data-testid={`attention-view-${item.lead_id}`}>
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {items.length > 5 && (
          <p className="text-[11px] text-muted-foreground">+{items.length - 5} more — open Sales → Leads to review the rest.</p>
        )}
      </CardContent>
    </Card>
  );
}
